import { useState } from 'react';
import { Link, NavLink, Outlet, useLocation } from 'react-router-dom';
import {
  Home as HomeIcon,
  UserPlus,
  Users as UsersIcon,
  Sparkles,
  Briefcase,
  Hourglass,
  RotateCcw,
  Download,
  CheckCircle2,
  BookOpen,
  LifeBuoy,
  ShieldCheck,
  RefreshCw,
  FileText,
  FileMinus2,
  FilePlus2,
  CircleSlash,
  FileStack,
  LogOut,
  Settings as SettingsIcon,
  type LucideIcon,
} from 'lucide-react';
import { useQueryClient, useIsFetching } from '@tanstack/react-query';
import { useCounts, useEtlStatus, useMe } from '@/api/hooks';
import { msalInstance, authDisabled } from '@/auth/msal';
import { useToast } from './Toast';
import { BrcLogo } from './BrcLogo';
import { AppWordmark } from './AppWordmark';
import { WelcomeTour } from './WelcomeTour';
import { NotificationBell } from './NotificationBell';

// Stamped at build time by vite.config.ts (release number from src/web/VERSION
// + git short hash + build date).
const APP_VERSION = __APP_VERSION__;

interface NavItem {
  to: string;
  label: string;
  icon: LucideIcon;
  end?: boolean;
  external?: string;
}

// Left nav, grouped into named sections (CAD Cleanup SS8, like Client Acceptance).
const MAIN: NavItem[] = [
  { to: '/', label: 'Dashboard', icon: HomeIcon, end: true },
];
const CLIENTS: NavItem[] = [
  { to: '/clients/new', label: 'New Client', icon: UserPlus },
  // Item 22: icons match the Dashboard tiles (Total Clients, Engagements via App, Total Engagements).
  { to: '/clients', label: 'View / Change Clients', icon: UsersIcon, end: true },
  { to: '/engagements/new', label: 'New Engagement', icon: FilePlus2 },
  { to: '/engagements', label: 'View / Change Engagements', icon: Briefcase, end: true },
];
const REQUESTS: NavItem[] = [
  { to: '/pending', label: 'Pending Requests', icon: Hourglass, end: true },
  { to: '/pending/download', label: 'Downloaded Requests', icon: Download },
  { to: '/completed', label: 'Completed Requests', icon: CheckCircle2 },
];
const FORMS: NavItem[] = [
  { to: '/forms/termination', label: 'Client Termination', icon: FileMinus2 },
  { to: '/forms/discontinuation', label: 'Engagement Discontinuation', icon: CircleSlash },
  { to: '/forms/reactivation', label: 'Client Reactivation', icon: RotateCcw },
  // Bulk change of client / engagement details: one request per file, worked in
  // Submitted Forms > Mass Change. Open to everyone. Address kept for existing links.
  { to: '/forms/mass-change', label: 'Mass Change Request', icon: FileStack },
];
// The request forms above are visible to all. The Submitted Forms log (with the
// admin checklists) is listed under Forms for admins only.
const SUBMITTED_FORMS: NavItem = { to: '/forms/submitted', label: 'Submitted Forms', icon: FileText };

/**
 * Maconomy data-load lock — mirrors Power Apps. While one of these reference
 * tables is loading, the app puts a translucent overlay over the data-entry and
 * approval screens so nothing can be created/edited/approved against data that
 * is mid-refresh. Read-only screens (lists, Completed, Download) stay usable.
 */
const ETL_MESSAGES: Record<string, string> = {
  tb_Client_Information: 'Maconomy client data load in progress. Kindly wait for it to complete',
  tb_Employee_Information: 'Maconomy employee data load in progress. Kindly wait for it to complete',
  tb_Engagement_Service_Type:
    'Maconomy engagement service type data load in progress. Kindly wait for it to complete',
  tb_Engagement_Location:
    'Maconomy engagement location data load in progress. Kindly wait for it to complete',
  tb_Client_Industry: 'Maconomy client industry data load in progress. Kindly wait for it to complete',
};

// Routes locked during a load (the screens Power Apps covers with its overlay):
// New/Edit Client, New/Edit Engagement, Pending Requests. The ".../additional"
// and "/pending/download" routes are read-only, so they are intentionally not locked.
const LOCKED_ROUTES: RegExp[] = [
  /^\/clients\/new$/,
  /^\/clients\/[^/]+\/[^/]+$/,
  /^\/engagements\/new$/,
  /^\/engagements\/[^/]+$/,
  /^\/pending$/,
];

const TITLES: Record<string, string> = {
  '/': 'Dashboard',
  '/settings': 'Settings',
  '/clients': 'View / Change Clients',
  '/clients/new': 'New Client',
  '/engagements': 'View / Change Engagements',
  '/engagements/new': 'New Engagement',
  '/pending': 'Pending Requests',
  '/pending/download': 'Downloaded Requests',
  '/help': 'Help',
  '/completed': 'Completed Requests',
  '/forms/termination': 'Client Termination',
  '/forms/discontinuation': 'Engagement Discontinuation',
  '/forms/reactivation': 'Client Reactivation',
  '/forms/mass-change': 'Mass Change Request',
  '/forms/submitted': 'Submitted Forms',
  '/users': 'Manage Users',
};

function MenuLink({ item }: { item: NavItem }) {
  const Icon = item.icon;
  return (
    <NavLink
      to={item.to}
      end={item.end}
      className={({ isActive }) =>
        `group flex items-center gap-3 rounded-lg px-3 py-1 h-md:py-1.5 h-lg:py-2 text-sm transition-colors ${
          isActive
            ? 'bg-sidebar-accent text-sidebar-primary'
            : 'text-sidebar-foreground/80 hover:bg-sidebar-accent hover:text-sidebar-foreground'
        }`
      }
    >
      <Icon className="h-5 w-5 shrink-0" />
      {item.label}
    </NavLink>
  );
}

// Small grey section label grouping nav links (CAD Cleanup SS8, like Client Acceptance).
function SectionHeader({ children }: { children: string }) {
  return (
    <p className="mb-0.5 h-md:mb-1 px-3 text-[10px] font-semibold uppercase tracking-wider text-sidebar-foreground/40">
      {children}
    </p>
  );
}

export function AppShell() {
  const { data: me } = useMe();
  const { data: counts } = useCounts();
  const { data: etl } = useEtlStatus();
  const location = useLocation();
  const title = TITLES[location.pathname] ?? (location.pathname.startsWith('/forms/submitted/') ? 'Submitted Forms' : '');

  // SS9: the Requests + Forms nav sections are admin-only. "Admin" = the user's role
  // in MasterSH_PowerApps.tb_Users (surfaced via me.roles), plus the super-admin flag.
  const isAdmin = !!me?.isSuperAdmin || (me?.roles ?? []).includes('Admin');
  const [tourOpen, setTourOpen] = useState(false);

  function logout() {
    if (authDisabled) return;
    msalInstance.logoutRedirect({ postLogoutRedirectUri: window.location.origin });
  }

  // Manual "refresh all data" — mirrors the Power App TitleBar globe-refresh icon,
  // which re-pulls every reference table + client/engagement view so an open session
  // isn't stuck on stale data (e.g. after a Maconomy load or another user's change).
  // One invalidateQueries() marks the whole cache stale; active screens refetch now.
  const qc = useQueryClient();
  const isFetching = useIsFetching();
  const notify = useToast();
  async function refreshAll() {
    await qc.invalidateQueries();
    notify('success', 'Data refreshed.');
  }

  // Which load is running (drives the exact message), and is this screen locked?
  const runningTable = (etl?.tables ?? []).find(
    (t) => ETL_MESSAGES[t.TableName] && t.LatestRunStatus === 'In Progress',
  );
  const etlMessage = runningTable ? ETL_MESSAGES[runningTable.TableName] : '';
  const screenLocked = !!etl?.inProgress && LOCKED_ROUTES.some((re) => re.test(location.pathname));

  return (
    // h-screen + overflow-hidden = app is exactly the viewport; the header/sidebar
    // stay fixed and each page scrolls inside <main>, so a wide table's horizontal
    // scrollbar stays visible instead of sitting at the bottom of a 500-row page.
    <div className="flex h-screen overflow-hidden bg-card">
      {/* Left menu runs the full height (like CRM), with the app wordmark on top. */}
      <aside className="flex w-80 shrink-0 flex-col border-r border-border bg-sidebar">
        <Link to="/" title="Dashboard" className="flex shrink-0 items-center justify-center border-b border-sidebar-border px-6 py-2 transition hover:opacity-80 h-md:py-4">
          <AppWordmark />
        </Link>
        <div className="flex min-h-0 flex-1 flex-col justify-between overflow-y-auto p-3">
          <nav className="space-y-2 h-md:space-y-3 h-lg:space-y-4">
            <div className="space-y-0.5 h-lg:space-y-1">
              {MAIN.map((i) => (
                <MenuLink key={i.to} item={i} />
              ))}
            </div>
            <div className="space-y-0.5 h-lg:space-y-1">
              <SectionHeader>Clients &amp; Engagements</SectionHeader>
              {CLIENTS.map((i) => (
                <MenuLink key={i.to} item={i} />
              ))}
            </div>
            {/* SS9: Requests + Forms are admin-only. */}
            {isAdmin && (
              <div className="space-y-0.5 h-lg:space-y-1">
                <SectionHeader>Requests</SectionHeader>
                {REQUESTS.map((i) => (
                  <MenuLink key={i.to} item={i} />
                ))}
              </div>
            )}
            {/* The request forms are visible to all users; Submitted Forms (with the
                admin checklists) shows here only for admins. */}
            <div className="space-y-0.5 h-lg:space-y-1">
              <SectionHeader>Forms</SectionHeader>
              {FORMS.map((i) => (
                <MenuLink key={i.to} item={i} />
              ))}
              {isAdmin && <MenuLink item={SUBMITTED_FORMS} />}
            </div>
            {/* CAD Cleanup SS6: Help / Resources / Take a Tour (like CRM). Resources opens
                the SharePoint page the old HELP item pointed to; Help and Take a Tour are
                placeholders until we decide what they point to. */}
            <div className="space-y-0.5 h-lg:space-y-1">
              <SectionHeader>Help</SectionHeader>
              <MenuLink item={{ to: '/help', label: 'Help', icon: LifeBuoy, end: true }} />
              <a
                href="https://brccpa.sharepoint.com/sites/PracticeManagement"
                target="_blank"
                rel="noreferrer"
                className="group flex items-center gap-3 rounded-lg px-3 py-1 h-md:py-1.5 h-lg:py-2 text-sm text-sidebar-foreground/80 hover:bg-sidebar-accent hover:text-sidebar-foreground"
              >
                <BookOpen className="h-5 w-5 shrink-0" />
                Resources
              </a>
              <button
                type="button"
                onClick={() => setTourOpen(true)}
                className="group flex w-full items-center gap-3 rounded-lg px-3 py-1 h-md:py-1.5 h-lg:py-2 text-left text-sm text-sidebar-foreground/80 hover:bg-sidebar-accent hover:text-sidebar-foreground"
              >
                <Sparkles className="h-5 w-5 shrink-0" />
                Take a Tour
              </button>
            </div>
            {isAdmin && (
              <div className="space-y-0.5 h-lg:space-y-1">
                <SectionHeader>Admin</SectionHeader>
                <MenuLink item={{ to: '/settings', label: 'Settings', icon: SettingsIcon, end: true }} />
                {me?.isSuperAdmin && (
                  <MenuLink item={{ to: '/users', label: 'Manage Users', icon: ShieldCheck, end: true }} />
                )}
              </div>
            )}
          </nav>

          <div className="space-y-2">
            {/* SS9: logged-in user + logout at the bottom, like CRM. */}
            <div className="border-t border-sidebar-border pt-2">
              <div className="truncate px-3 py-1 text-xs font-medium text-sidebar-foreground/70">
                {me?.name ?? '—'}
              </div>
              {!authDisabled && (
                <button
                  type="button"
                  onClick={logout}
                  className="group flex w-full items-center gap-3 rounded-lg px-3 py-1 h-md:py-1.5 h-lg:py-2 text-left text-sm text-sidebar-foreground/80 hover:bg-sidebar-accent hover:text-sidebar-foreground"
                >
                  <LogOut className="h-5 w-5 shrink-0" />
                  Logout
                </button>
              )}
            </div>
          </div>
        </div>
      </aside>

      {/* Right column: header (page title left; refresh, bell and BRC logo right), then the page. */}
      <div className="flex min-w-0 flex-1 flex-col">
        <div className="flex h-[72px] shrink-0 items-center justify-between gap-4 border-b border-border px-6">
          <h1 className="truncate text-xl font-semibold text-foreground">{title}</h1>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={refreshAll}
            title="Refresh all data"
            aria-label="Refresh all data"
            className="flex h-9 w-9 items-center justify-center rounded-full text-primary transition-colors hover:bg-secondary disabled:opacity-60"
            disabled={!!isFetching}
          >
            <RefreshCw className={`h-5 w-5 ${isFetching ? 'animate-spin' : ''}`} />
          </button>
          {isAdmin && (
            <NotificationBell
              pendingClients={counts?.pendingClients ?? 0}
              pendingEngagements={counts?.pendingEngagements ?? 0}
            />
          )}
          <Link to="/" title="Dashboard" className="ml-2 transition hover:opacity-80">
            <BrcLogo className="h-8" />
          </Link>
        </div>
        </div>

      {etl?.inProgress ? (
        <div className="bg-warning px-6 py-2 text-sm font-medium text-warning-foreground">
          {etlMessage || 'Maconomy data load in progress. Kindly wait for it to complete'} — creating,
          editing and approving are disabled until it finishes.
        </div>
      ) : null}

        {/* min-w-0 lets this flex child shrink below its content's intrinsic width,
            so wide content scrolls inside its own container instead of pushing the
            whole page wider than the viewport (horizontal scrollbar on small displays).
            While locked we stop scrolling so the absolute overlay covers the whole area. */}
        <main
          className={`relative min-h-0 min-w-0 flex-1 bg-muted/40 ${screenLocked ? 'overflow-hidden' : 'overflow-auto'}`}
        >
          <Outlet />
          {screenLocked && (
            <div
              className="absolute inset-0 z-40 flex items-start justify-center bg-muted/80 p-8 backdrop-blur-[1px]"
              role="alert"
              aria-live="polite"
            >
              <div className="card mt-8 max-w-xl text-center">
                <p className="font-semibold text-primary">{etlMessage}</p>
                <p className="mt-2 text-sm text-muted-foreground">
                  This screen is temporarily locked while Maconomy reference data is refreshed. It
                  unlocks automatically when the load finishes.
                </p>
              </div>
            </div>
          )}
        </main>

        <footer className="flex items-center justify-between border-t border-border px-4 py-1.5 text-xs text-muted-foreground">
          <span>© 2026 BRC. All rights reserved.</span>
          <span>Version: {APP_VERSION}</span>
        </footer>
      </div>

      <WelcomeTour open={tourOpen} onClose={() => setTourOpen(false)} isAdmin={isAdmin} />
    </div>
  );
}
